import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Plane, Briefcase, Star, Check, ChevronLeft, ChevronRight, Phone, Mail, MapPin, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import SEO from "@/components/SEO";

// High-quality images from project assets
import heroImage from "@/assets/audi-downtown-night.png";
import blackSuv1 from "@/assets/audi-luxury.jpg";
import blackSuv2 from "@/assets/audi-hotel-emma.png";
import blackSuv3 from "@/assets/audi-downtown.png";
import blackSuv4 from "@/assets/audi-sunset.jpg";
import blackSuv5 from "@/assets/audi-night-city.jpeg";
import sanAntonioSkyline from "@/assets/san-antonio-riverwalk.jpg";

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentFleetIndex, setCurrentFleetIndex] = useState(0);
  const [isFleetPaused, setIsFleetPaused] = useState(false);

  // Smooth scroll behavior for anchor links
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href^="#"]');
      if (anchor) {
        e.preventDefault();
        const href = anchor.getAttribute('href');
        if (href) {
          const element = document.querySelector(href);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);

  const services = [
    {
      icon: Plane,
      title: "Airport Transfers",
      description: "Seamless SAT Airport pickups and drop-offs with flight tracking and meet-and-greet service."
    },
    {
      icon: Briefcase,
      title: "Corporate Travel",
      description: "Impress clients and executives with punctual, professional transportation services."
    },
    {
      icon: Star,
      title: "Hourly / Special Events",
      description: "Weddings, galas, wine tours—arrive in style for life's most memorable moments."
    }
  ];

  const benefits = [
    "Flat Rates — No surge pricing or hidden fees",
    "24/7 Availability — Day or night, we're ready",
    "Professional Chauffeurs — Background-checked, impeccably dressed"
  ];

  const fleet = [
    {
      name: "2025 Audi Q7 Black Optic",
      image: blackSuv1,
      capacity: "Up to 6 passengers",
      description: "Premium luxury SUV with spacious interior"
    },
    {
      name: "Executive Downtown Service",
      image: blackSuv2,
      capacity: "Up to 6 passengers",
      description: "Elegant arrivals at San Antonio's finest venues"
    },
    {
      name: "Corporate Fleet",
      image: blackSuv3,
      capacity: "Up to 6 passengers",
      description: "Professional transportation for business travelers"
    },
    {
      name: "Sunset Tours",
      image: blackSuv4,
      capacity: "Up to 6 passengers",
      description: "Hill Country wine tours in ultimate comfort"
    },
    {
      name: "Night Life Service",
      image: blackSuv5,
      capacity: "Up to 6 passengers",
      description: "Safe, stylish transportation for evening events"
    }
  ];

  const nextFleet = () => setCurrentFleetIndex((prev) => (prev + 1) % fleet.length);
  const prevFleet = () => setCurrentFleetIndex((prev) => (prev - 1 + fleet.length) % fleet.length);

  // Preload all fleet images for smooth, seamless transitions
  useEffect(() => {
    fleet.forEach((vehicle) => {
      const img = new Image();
      img.src = vehicle.image;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Elegant autoplay — pauses on hover/focus
  useEffect(() => {
    if (isFleetPaused) return;
    const id = window.setInterval(() => {
      setCurrentFleetIndex((prev) => (prev + 1) % fleet.length);
    }, 6000);
    return () => window.clearInterval(id);
  }, [isFleetPaused, fleet.length]);

  return (
    <>
      <SEO
        title="Rick's Black Car Service San Antonio | Airport & Limo Service"
        description="Professional, reliable luxury transportation for SAT Airport, corporate travel, and special events in San Antonio, TX and the surrounding Hill Country."
        canonicalPath="/landing"
        keywords="black car service san antonio, airport transportation sat, limo service san antonio, corporate car service, luxury transportation texas"
      />

      <div className="min-h-screen bg-landing-background text-landing-foreground font-sans">
        {/* Sticky Navbar */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-landing-background/80 backdrop-blur-md border-b border-white/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16 md:h-20">
              {/* Logo */}
              <Link to="/" className="flex items-center gap-2">
                <span className="font-serif text-xl md:text-2xl font-bold text-white">
                  Rick's <span className="text-landing-gold">Black Car</span>
                </span>
              </Link>

              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center gap-8">
                <a href="#services" className="text-white/80 hover:text-white transition-colors text-sm uppercase tracking-wider">
                  Services
                </a>
                <a href="#fleet" className="text-white/80 hover:text-white transition-colors text-sm uppercase tracking-wider">
                  Fleet
                </a>
                <a href="#contact" className="text-white/80 hover:text-white transition-colors text-sm uppercase tracking-wider">
                  Contact
                </a>
                <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">
                  <Button className="bg-landing-gold hover:bg-landing-gold-hover text-landing-background font-semibold px-6">
                    Book Now
                  </Button>
                </a>
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden text-white p-2"
                aria-label="Toggle navigation menu"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>

            {/* Mobile Menu */}
            {mobileMenuOpen && (
              <div className="md:hidden py-4 border-t border-white/10">
                <div className="flex flex-col gap-4">
                  <a href="#services" className="text-white/80 hover:text-white transition-colors text-sm uppercase tracking-wider py-2">
                    Services
                  </a>
                  <a href="#fleet" className="text-white/80 hover:text-white transition-colors text-sm uppercase tracking-wider py-2">
                    Fleet
                  </a>
                  <a href="#contact" className="text-white/80 hover:text-white transition-colors text-sm uppercase tracking-wider py-2">
                    Contact
                  </a>
                  <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">
                    <Button className="bg-landing-gold hover:bg-landing-gold-hover text-landing-background font-semibold w-full">
                      Book Now
                    </Button>
                  </a>
                </div>
              </div>
            )}
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center pt-20">
          {/* Background Image with Overlay */}
          <div className="absolute inset-0 z-0">
            <img
              src={heroImage}
              alt="Luxury black car service"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-landing-background/90 via-landing-background/70 to-landing-background" />
          </div>

          {/* Hero Content */}
          <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Rick's Black Car Service
              <span className="block text-landing-gold mt-2">San Antonio</span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-white/80 max-w-3xl mx-auto mb-8 leading-relaxed">
              Professional, reliable luxury transportation for SAT Airport, corporate travel, and special events.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">
                <Button
                  size="lg"
                  className="bg-landing-gold hover:bg-landing-gold-hover text-landing-background font-semibold px-8 py-6 text-lg"
                >
                  Get a Quote
                </Button>
              </a>
              <Button
                size="lg"
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg"
              >
                <Phone className="mr-2 h-5 w-5" />
                Call Now
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="mt-12 flex flex-wrap justify-center gap-8 text-white/60 text-sm">
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-landing-gold" />
                <span>Flat Rate Pricing</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-landing-gold" />
                <span>24/7 Service</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-landing-gold" />
                <span>Professional Chauffeurs</span>
              </div>
            </div>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
              <div className="w-1 h-2 bg-landing-gold rounded-full animate-pulse" />
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section id="services" className="py-20 md:py-32 bg-landing-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
                Our <span className="text-landing-gold">Services</span>
              </h2>
              <p className="text-white/60 max-w-2xl mx-auto text-lg">
                Experience the pinnacle of luxury ground transportation
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <Card
                  key={index}
                  className="bg-white/5 border-white/10 hover:border-landing-gold/50 transition-all duration-300 group"
                >
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-landing-gold/10 flex items-center justify-center group-hover:bg-landing-gold/20 transition-colors">
                      <service.icon className="h-8 w-8 text-landing-gold" />
                    </div>
                    <h3 className="font-serif text-2xl font-bold text-white mb-4">
                      {service.title}
                    </h3>
                    <p className="text-white/60 leading-relaxed">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 md:py-32 bg-gradient-to-b from-landing-background to-landing-card">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-8">
                  Why Choose <span className="text-landing-gold">Rick's</span>?
                </h2>
                <div className="space-y-6">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-landing-gold flex items-center justify-center">
                        <Check className="h-5 w-5 text-landing-background" />
                      </div>
                      <p className="text-white/80 text-lg leading-relaxed pt-1">
                        {benefit}
                      </p>
                    </div>
                  ))}
                </div>
                <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">
                  <Button
                    size="lg"
                    className="mt-10 bg-landing-gold hover:bg-landing-gold-hover text-landing-background font-semibold px-8"
                  >
                    Book Your Ride
                  </Button>
                </a>
              </div>
              <div className="relative">
                <img
                  src={sanAntonioSkyline}
                  alt="San Antonio skyline at night"
                  className="rounded-lg shadow-2xl"
                />
                <div className="absolute -bottom-6 -left-6 bg-landing-gold text-landing-background p-6 rounded-lg shadow-xl">
                  <p className="font-serif text-3xl font-bold">10+</p>
                  <p className="text-sm uppercase tracking-wider">Years Experience</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Fleet Preview */}
        <section id="fleet" className="py-20 md:py-32 bg-landing-card">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
                Our <span className="text-landing-gold">Fleet</span>
              </h2>
              <p className="text-white/60 max-w-2xl mx-auto text-lg">
                Meticulously maintained vehicles for your comfort and safety
              </p>
            </div>

            {/* Fleet Carousel */}
            <div
              className="relative max-w-4xl mx-auto"
              onMouseEnter={() => setIsFleetPaused(true)}
              onMouseLeave={() => setIsFleetPaused(false)}
              onFocusCapture={() => setIsFleetPaused(true)}
              onBlurCapture={() => setIsFleetPaused(false)}
            >
              <div className="overflow-hidden rounded-xl shadow-2xl ring-1 ring-white/10 bg-landing-background">
                <div className="relative aspect-video">
                  {fleet.map((vehicle, index) => {
                    const isActive = index === currentFleetIndex;
                    return (
                      <div
                        key={index}
                        className={`absolute inset-0 transition-opacity ease-in-out will-change-[opacity] ${
                          isActive
                            ? "opacity-100 z-10 duration-[1500ms]"
                            : "opacity-0 z-0 duration-[1500ms]"
                        }`}
                        aria-hidden={!isActive}
                      >
                        <img
                          src={vehicle.image}
                          alt={vehicle.name}
                          loading={index === 0 ? "eager" : "lazy"}
                          decoding="async"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-landing-background via-landing-background/30 to-transparent" />
                        <div
                          className={`absolute bottom-0 left-0 right-0 p-6 md:p-8 transition-all ease-in-out ${
                            isActive
                              ? "opacity-100 translate-y-0 duration-[1200ms] delay-200"
                              : "opacity-0 translate-y-3 duration-[700ms]"
                          }`}
                        >
                          <h3 className="font-serif text-2xl md:text-3xl font-bold text-white mb-2">
                            {vehicle.name}
                          </h3>
                          <p className="text-landing-gold font-medium mb-1">{vehicle.capacity}</p>
                          <p className="text-white/60">{vehicle.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Carousel Controls */}
              <button
                onClick={prevFleet}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                aria-label="Previous vehicle"
              >
                <ChevronLeft className="h-6 w-6 text-white" />
              </button>
              <button
                onClick={nextFleet}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                aria-label="Next vehicle"
              >
                <ChevronRight className="h-6 w-6 text-white" />
              </button>

              {/* Carousel Dots */}
              <div className="flex justify-center gap-3 mt-6">
                {fleet.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentFleetIndex(index)}
                    className={`h-2 rounded-full transition-all duration-500 ease-out ${
                      index === currentFleetIndex
                        ? "w-8 bg-landing-gold"
                        : "w-2 bg-white/30 hover:bg-white/50"
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 md:py-32 bg-landing-gold">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-landing-background mb-6">
              Ready to Experience Luxury?
            </h2>
            <p className="text-landing-background/80 max-w-2xl mx-auto text-lg mb-10">
              Book your premium transportation today and arrive in style.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">
                <Button
                  size="lg"
                  className="bg-landing-background hover:bg-landing-background/90 text-white font-semibold px-8 py-6 text-lg"
                >
                  Book Online
                </Button>
              </a>
              <Button
                size="lg"
                variant="outline"
                className="border-landing-background text-landing-background hover:bg-landing-background/10 px-8 py-6 text-lg"
              >
                <Phone className="mr-2 h-5 w-5" />
                (210) 296-7409
              </Button>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer id="contact" className="py-16 bg-landing-background border-t border-white/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              {/* Brand */}
              <div>
                <h3 className="font-serif text-2xl font-bold text-white mb-4">
                  Rick's <span className="text-landing-gold">Black Car</span>
                </h3>
                <p className="text-white/60 leading-relaxed">
                  Premium black car service serving San Antonio, TX and the surrounding Hill Country since 2014.
                </p>
              </div>

              {/* Contact */}
              <div>
                <h4 className="font-semibold text-white uppercase tracking-wider text-sm mb-4">
                  Contact
                </h4>
                <div className="space-y-3">
                  <a href="tel:+12102967409" className="flex items-center gap-3 text-white/60 hover:text-landing-gold transition-colors">
                    <Phone className="h-4 w-4" />
                    (210) 296-7409
                  </a>
                  <a href="mailto:info@ricksblackcarservice.com" className="flex items-center gap-3 text-white/60 hover:text-landing-gold transition-colors">
                    <Mail className="h-4 w-4" />
                    info@ricksblackcarservice.com
                  </a>
                  <div className="flex items-start gap-3 text-white/60">
                    <MapPin className="h-4 w-4 mt-1 flex-shrink-0" />
                    <span>Serving San Antonio, TX & Hill Country</span>
                  </div>
                </div>
              </div>

              {/* Links */}
              <div>
                <h4 className="font-semibold text-white uppercase tracking-wider text-sm mb-4">
                  Quick Links
                </h4>
                <div className="space-y-3">
                  <Link to="/services" className="block text-white/60 hover:text-landing-gold transition-colors">
                    Services
                  </Link>
                  <Link to="/fleet" className="block text-white/60 hover:text-landing-gold transition-colors">
                    Our Fleet
                  </Link>
                  <Link to="/about" className="block text-white/60 hover:text-landing-gold transition-colors">
                    About Us
                  </Link>
                  <Link to="/contact" className="block text-white/60 hover:text-landing-gold transition-colors">
                    Contact
                  </Link>
                </div>
              </div>
            </div>

            {/* Bottom Bar */}
            <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-white/40 text-sm">
                © {new Date().getFullYear()} Rick's Black Car Service. All rights reserved.
              </p>
              <div className="flex gap-6 text-sm">
                <Link to="/privacy" className="text-white/40 hover:text-white/60 transition-colors">
                  Privacy Policy
                </Link>
                <Link to="/terms" className="text-white/40 hover:text-white/60 transition-colors">
                  Terms of Service
                </Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LandingPage;
